import {
  ActionService_ListActions,
  type ListActionsRequest,
  type ListActionsResponse,
} from '@src/gen/ai/h2o/authorization/v1/action_api_pb';
import {
  type ListPoliciesRequest,
  type ListPoliciesResponse,
  PolicyService_ListPolicies,
} from '@src/gen/ai/h2o/authorization/v1/policy_api_pb';
import {
  type BatchResolvePermissionsResponse,
  QueryService_BatchResolvePermissions,
  type ResolvePermissionRequest,
} from '@src/gen/ai/h2o/authorization/v1/query_api_pb';
import {
  type AddActionStatementsRequest,
  type AddActionStatementsResponse,
  type CreateRoleRequest,
  type CreateRoleResponse,
  type DeleteRoleRequest,
  type DeleteRoleResponse,
  type GetRoleRequest,
  type GetRoleResponse,
  type ListRolesRequest,
  type ListRolesResponse,
  type RemoveActionStatementsRequest,
  type RemoveActionStatementsResponse,
  RoleService_AddActionStatements,
  RoleService_CreateRole,
  RoleService_DeleteRole,
  RoleService_GetRole,
  RoleService_ListRoles,
  RoleService_RemoveActionStatements,
  RoleService_UpdateRole,
  type UpdateRoleRequest,
  type UpdateRoleResponse,
} from '@src/gen/ai/h2o/authorization/v1/role_api_pb';
import {
  type CreateRoleBindingRequest,
  type CreateRoleBindingResponse,
  type DeleteRoleBindingRequest,
  type DeleteRoleBindingResponse,
  type GetRoleBindingResponse,
  type ListRoleBindingsRequest,
  type ListRoleBindingsResponse,
  RoleBindingService_CreateRoleBinding,
  RoleBindingService_DeleteRoleBinding,
  RoleBindingService_GetRoleBinding,
  RoleBindingService_ListRoleBindings,
} from '@src/gen/ai/h2o/authorization/v1/role_binding_api_pb';
import {
  type GetGroupResponse,
  GroupService_GetGroup,
  GroupService_ListGroups,
  GroupService_ListUserGroups,
  type ListGroupsRequest,
  type ListGroupsResponse,
  type ListUserGroupsRequest,
  type ListUserGroupsResponse,
} from '@src/gen/ai/h2o/user/v1/group_api_pb';
import {
  type GetUserRequest,
  type GetUserResponse,
  type ListUsersRequest,
  type ListUsersResponse,
  UserService_GetUser,
  UserService_ListUsers,
} from '@src/gen/ai/h2o/user/v1/user_api_pb';
import {
  type CreateLabelRequest,
  type CreateLabelResponse,
  type DeleteLabelRequest,
  type DeleteLabelResponse,
  type GetLabelRequest,
  type GetLabelResponse,
  LabelService_CreateLabel,
  LabelService_DeleteLabel,
  LabelService_GetLabel,
  LabelService_ListLabels,
  LabelService_UpdateLabel,
  type ListLabelsRequest,
  type ListLabelsResponse,
  type UpdateLabelRequest,
  type UpdateLabelResponse,
} from '@src/gen/ai/h2o/workspace/v1/label_api_pb';
import {
  type CreateTagRequest,
  type CreateTagResponse,
  type DeleteTagRequest,
  type DeleteTagResponse,
  type GetTagRequest,
  type GetTagResponse,
  type ListTagsRequest,
  type ListTagsResponse,
  TagService_CreateTag,
  TagService_DeleteTag,
  TagService_GetTag,
  TagService_ListTags,
  TagService_UpdateTag,
  type UpdateTagRequest,
  type UpdateTagResponse,
} from '@src/gen/ai/h2o/workspace/v1/tag_api_pb';
import {
  type AddLabelsRequest,
  type AddLabelsResponse,
  type AddTagsRequest,
  type AddTagsResponse,
  type ArchiveWorkspaceResponse,
  type CreateWorkspaceRequest,
  type CreateWorkspaceResponse,
  type DeleteWorkspaceResponse,
  type GetWorkspaceResponse,
  type ListAllWorkspacesRequest,
  type ListAllWorkspacesResponse,
  type ListWorkspacesRequest,
  type ListWorkspacesResponse,
  type RemoveLabelsRequest,
  type RemoveLabelsResponse,
  type RemoveTagsRequest,
  type RemoveTagsResponse,
  type UnarchiveWorkspaceResponse,
  type UndeleteWorkspaceResponse,
  type UpdateWorkspaceRequest,
  type UpdateWorkspaceResponse,
  WorkspaceService_AddLabels,
  WorkspaceService_AddTags,
  WorkspaceService_ArchiveWorkspace,
  WorkspaceService_CreateWorkspace,
  WorkspaceService_DeleteWorkspace,
  WorkspaceService_GetWorkspace,
  WorkspaceService_ListAllWorkspaces,
  WorkspaceService_ListWorkspaces,
  WorkspaceService_RemoveLabels,
  WorkspaceService_RemoveTags,
  WorkspaceService_UnarchiveWorkspace,
  WorkspaceService_UndeleteWorkspace,
  WorkspaceService_UpdateWorkspace,
} from '@src/gen/ai/h2o/workspace/v1/workspace_api_pb';
import { fetchWrapRPC } from '@shared-legacy/services/appstore/api';

// Groups

export const getGroup = (
  basePath: string,
  name: string,
  requestInit?: { signal?: AbortSignal }
): Promise<GetGroupResponse> => {
  return fetchWrapRPC(GroupService_GetGroup, { basePath })({ name }, requestInit);
};

export const listGroups = (
  basePath: string,
  req: ListGroupsRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<ListGroupsResponse> => {
  return fetchWrapRPC(GroupService_ListGroups, { basePath })(req, requestInit);
};

export const listUserGroups = (
  basePath: string,
  req: ListUserGroupsRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<ListUserGroupsResponse> => {
  return fetchWrapRPC(GroupService_ListUserGroups, { basePath })(req, requestInit);
};

// Tags

export const listTags = (
  basePath: string,
  req: ListTagsRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<ListTagsResponse> => {
  return fetchWrapRPC(TagService_ListTags, { basePath })(req, requestInit);
};

export const getTag = (
  basePath: string,
  req: GetTagRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<GetTagResponse> => {
  return fetchWrapRPC(TagService_GetTag, { basePath })(req, requestInit);
};

export const createTag = (
  basePath: string,
  req: CreateTagRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<CreateTagResponse> => {
  return fetchWrapRPC(TagService_CreateTag, { basePath })(req, requestInit);
};

export const updateTag = (
  basePath: string,
  req: UpdateTagRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<UpdateTagResponse> => {
  return fetchWrapRPC(TagService_UpdateTag, { basePath })(req, requestInit);
};

export const deleteTag = (
  basePath: string,
  req: DeleteTagRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<DeleteTagResponse> => {
  return fetchWrapRPC(TagService_DeleteTag, { basePath })(req, requestInit);
};

// Labels

export const listLabels = (
  basePath: string,
  req: ListLabelsRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<ListLabelsResponse> => {
  return fetchWrapRPC(LabelService_ListLabels, { basePath })(req, requestInit);
};

export const getLabel = (
  basePath: string,
  req: GetLabelRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<GetLabelResponse> => {
  return fetchWrapRPC(LabelService_GetLabel, { basePath })(req, requestInit);
};

export const createLabel = (
  basePath: string,
  req: CreateLabelRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<CreateLabelResponse> => {
  return fetchWrapRPC(LabelService_CreateLabel, { basePath })(req, requestInit);
};

export const updateLabel = (
  basePath: string,
  req: UpdateLabelRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<UpdateLabelResponse> => {
  return fetchWrapRPC(LabelService_UpdateLabel, { basePath })(req, requestInit);
};

export const deleteLabel = (
  basePath: string,
  req: DeleteLabelRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<DeleteLabelResponse> => {
  return fetchWrapRPC(LabelService_DeleteLabel, { basePath })(req, requestInit);
};

// Permissions

export type FixedResolvePermissionRequest = Pick<ResolvePermissionRequest, `context`> &
  Pick<Required<ResolvePermissionRequest>, `action` | `resource` | `subject`>;

export const batchResolvePermissions = (
  basePath: string,
  req: { requests: FixedResolvePermissionRequest[] },
  requestInit?: { signal?: AbortSignal }
): Promise<BatchResolvePermissionsResponse> => {
  return fetchWrapRPC(QueryService_BatchResolvePermissions, { basePath })(req, requestInit);
};

// Role Bindings

export const getRoleBinding = (
  basePath: string,
  name: string,
  requestInit?: { signal?: AbortSignal }
): Promise<GetRoleBindingResponse> => {
  return fetchWrapRPC(RoleBindingService_GetRoleBinding, { basePath })({ name }, requestInit);
};

/**
 * These fields are supported in the ListRoleBindings request for filtering
 * @see https://github.com/h2oai/authz-server/blob/2adc6fdd841ea70eaca2b55617766003cee506f1/api/proto/ai/h2o/authorization/v1/role_binding_api.proto#L111-L112
 */
export type ListRoleBindingsSupportedFilterKeys = `resource` | `subject`;

export const listRoleBindings = (
  basePath: string,
  req: ListRoleBindingsRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<ListRoleBindingsResponse> => {
  return fetchWrapRPC(RoleBindingService_ListRoleBindings, { basePath })(req, requestInit);
};

export const createRoleBinding = (
  basePath: string,
  req: CreateRoleBindingRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<CreateRoleBindingResponse> => {
  return fetchWrapRPC(RoleBindingService_CreateRoleBinding, { basePath })(req, requestInit);
};

export const deleteRoleBinding = (
  basePath: string,
  req: DeleteRoleBindingRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<DeleteRoleBindingResponse> => {
  return fetchWrapRPC(RoleBindingService_DeleteRoleBinding, { basePath })(req, requestInit);
};

const resourcePrefix = `//workspaceserver/`;

export const roleBindingResourceCompile = (resourceName: string) => `${resourcePrefix}${resourceName}`;

export const roleBindingResourceParse = (resource: string) => resource.slice(resourcePrefix.length);

/** Resource for permission checks that apply across workspaces (e.g. All Runs page). */
export const WORKSPACE_RESOURCE_WILDCARD = roleBindingResourceCompile('workspaces/-');

// Users

export const getUser = (
  basePath: string,
  req: GetUserRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<GetUserResponse> => {
  return fetchWrapRPC(UserService_GetUser, { basePath })(req, requestInit);
};

export const listUsers = (
  basePath: string,
  req: ListUsersRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<ListUsersResponse> => {
  return fetchWrapRPC(UserService_ListUsers, { basePath })(req, requestInit);
};

// Workspaces

export const createWorkspace = (
  basePath: string,
  req: CreateWorkspaceRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<CreateWorkspaceResponse> => {
  return fetchWrapRPC(WorkspaceService_CreateWorkspace, { basePath })(req, requestInit);
};

export const deleteWorkspace = (
  basePath: string,
  name: string,
  requestInit?: { signal?: AbortSignal }
): Promise<DeleteWorkspaceResponse> => {
  return fetchWrapRPC(WorkspaceService_DeleteWorkspace, { basePath })({ name }, requestInit);
};

export const archiveWorkspace = (
  basePath: string,
  name: string,
  requestInit?: { signal?: AbortSignal }
): Promise<ArchiveWorkspaceResponse> => {
  return fetchWrapRPC(WorkspaceService_ArchiveWorkspace, { basePath })({ name }, requestInit);
};

export const getWorkspace = (
  basePath: string,
  name: string,
  requestInit?: { signal?: AbortSignal }
): Promise<GetWorkspaceResponse> => {
  return fetchWrapRPC(WorkspaceService_GetWorkspace, { basePath })({ name }, requestInit);
};

/**
 * These fields are supported in the ListWorkspace request for filtering and sorting
 * @see https://github.com/h2oai/authz-server/blob/25d7f9c3b778a7221db14ff04ff4aad07c794e80/api/proto/ai/h2o/workspace/v1/workspace_api.proto#L186-L199
 */
export type ListWorkspaceSupportedFilterOrderKeys =
  | `display_name`
  | `description`
  | `create_time`
  | `update_time`
  | `creator`
  | `updater`
  | `personal_workspace_subject`
  | `annotations`
  | `delete_time`
  | `deleter`
  | `purge_time`
  | `archiving_time`
  | `archiver`;

export const listWorkspaces = (
  basePath: string,
  req: ListWorkspacesRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<ListWorkspacesResponse> => {
  return fetchWrapRPC(WorkspaceService_ListWorkspaces, { basePath })(req, requestInit);
};

export const listAllWorkspaces = (
  basePath: string,
  req: ListAllWorkspacesRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<ListAllWorkspacesResponse> => {
  return fetchWrapRPC(WorkspaceService_ListAllWorkspaces, { basePath })(req, requestInit);
};

export const undeleteWorkspace = (
  basePath: string,
  name: string,
  requestInit?: { signal?: AbortSignal }
): Promise<UndeleteWorkspaceResponse> => {
  return fetchWrapRPC(WorkspaceService_UndeleteWorkspace, { basePath })({ name }, requestInit);
};

export const unarchiveWorkspace = (
  basePath: string,
  name: string,
  requestInit?: { signal?: AbortSignal }
): Promise<UnarchiveWorkspaceResponse> => {
  return fetchWrapRPC(WorkspaceService_UnarchiveWorkspace, { basePath })({ name }, requestInit);
};

export const updateWorkspace = (
  basePath: string,
  req: UpdateWorkspaceRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<UpdateWorkspaceResponse> => {
  return fetchWrapRPC(WorkspaceService_UpdateWorkspace, { basePath })(req, requestInit);
};

export const addTagsToWorkspace = (
  basePath: string,
  req: AddTagsRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<AddTagsResponse> => {
  return fetchWrapRPC(WorkspaceService_AddTags, { basePath })(req, requestInit);
};

export const removeTagsFromWorkspace = (
  basePath: string,
  req: RemoveTagsRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<RemoveTagsResponse> => {
  return fetchWrapRPC(WorkspaceService_RemoveTags, { basePath })(req, requestInit);
};

export const addLabelsToWorkspace = (
  basePath: string,
  req: AddLabelsRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<AddLabelsResponse> => {
  return fetchWrapRPC(WorkspaceService_AddLabels, { basePath })(req, requestInit);
};

export const removeLabelsFromWorkspace = (
  basePath: string,
  req: RemoveLabelsRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<RemoveLabelsResponse> => {
  return fetchWrapRPC(WorkspaceService_RemoveLabels, { basePath })(req, requestInit);
};

// Policies

export const listPolicies = (
  basePath: string,
  req: ListPoliciesRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<ListPoliciesResponse> => {
  return fetchWrapRPC(PolicyService_ListPolicies, { basePath })(req, requestInit);
};

// Roles

export const listRoles = (
  basePath: string,
  req: ListRolesRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<ListRolesResponse> => {
  return fetchWrapRPC(RoleService_ListRoles, { basePath })(req, requestInit);
};

export const getRole = (
  basePath: string,
  req: GetRoleRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<GetRoleResponse> => {
  return fetchWrapRPC(RoleService_GetRole, { basePath })(req, requestInit);
};

export const createRole = (
  basePath: string,
  req: CreateRoleRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<CreateRoleResponse> => {
  return fetchWrapRPC(RoleService_CreateRole, { basePath })(req, requestInit);
};

export const updateRole = (
  basePath: string,
  req: UpdateRoleRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<UpdateRoleResponse> => {
  return fetchWrapRPC(RoleService_UpdateRole, { basePath })(req, requestInit);
};

export const deleteRole = (
  basePath: string,
  req: DeleteRoleRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<DeleteRoleResponse> => {
  return fetchWrapRPC(RoleService_DeleteRole, { basePath })(req, requestInit);
};

export const addRoleActionStatements = (
  basePath: string,
  req: AddActionStatementsRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<AddActionStatementsResponse> => {
  return fetchWrapRPC(RoleService_AddActionStatements, { basePath })(req, requestInit);
};

export const removeRoleActionStatements = (
  basePath: string,
  req: RemoveActionStatementsRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<RemoveActionStatementsResponse> => {
  return fetchWrapRPC(RoleService_RemoveActionStatements, { basePath })(req, requestInit);
};

// Actions catalog

export const listActions = (
  basePath: string,
  req: ListActionsRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<ListActionsResponse> => {
  return fetchWrapRPC(ActionService_ListActions, { basePath })(req, requestInit);
};
